import { useTranslation } from 'react-i18next';
import Slider from 'react-slick';
import { useState, useRef } from 'react';

const BLUE = '#2563eb'; // A strong blue

// Staff carousel arrows - using same design as Services arrows
const StaffArrowLeft = (props: any) => (
  <button
    {...props}
    className="slick-arrow slick-prev !z-50 absolute top-1/2 -translate-y-1/2 left-[-40px] md:left-[-56px] w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center transition-all duration-200 focus:outline-none shadow-lg hover:scale-110 hover:shadow-2xl"
    aria-label="Previous"
    style={{ ...props.style, display: 'block', border: 'none' }}
  >
    <svg className="w-12 h-12 md:w-16 md:h-16" fill="none" stroke="white" strokeWidth="4" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  </button>
);
const StaffArrowRight = (props: any) => (
  <button
    {...props}
    className="slick-arrow slick-next !z-50 absolute top-1/2 -translate-y-1/2 right-[-40px] md:right-[-56px] w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center transition-all duration-200 focus:outline-none shadow-lg hover:scale-110 hover:shadow-2xl"
    aria-label="Next"
    style={{ ...props.style, display: 'block', border: 'none' }}
  >
    <svg className="w-12 h-12 md:w-16 md:h-16" fill="none" stroke="white" strokeWidth="4" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  </button>
);

interface StaffMember {
  id: string;
  name: string;
  title: string;
  description: string;
  image: string;
}

const StaffCard = ({ member }: { member: StaffMember }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation();
  const maxLength = 150;
  const shouldShowButton = member.description.length > maxLength;

  return (
    <div className="h-[500px] mx-2 bg-white rounded-2xl shadow-lg overflow-hidden transform transition-all duration-300 hover:-translate-y-1 flex flex-col">
      <div className={`relative transition-all duration-300 ${isExpanded ? 'h-24' : 'h-64'}`}> 
        <img
          src={member.image}
          alt={member.name}
          className="w-full h-full object-cover"
        />
      </div>
      <div className="p-6 flex flex-col flex-1">
        <h3 className="text-xl font-bold text-primary-dark mb-2">{member.name}</h3>
        <h4 className="text-lg text-gray-600 mb-3">{member.title}</h4>
        <div className="flex-1">
          <p className={`text-gray-500 text-sm ${!isExpanded && 'line-clamp-3'}`}>{member.description}</p>
          {shouldShowButton && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="mt-2 text-primary-dark font-semibold text-sm hover:text-primary-dark/80 transition-colors"
            >
              {isExpanded ? t('staff.seeLess') : t('staff.seeMore')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const desktopSliderSettings = {
  dots: true,
  infinite: true,
  speed: 500,
  slidesToShow: 4,
  slidesToScroll: 1,
  rtl: false,
  appendDots: (dots: any) => (
    <div style={{ marginTop: '2.5rem' }}>
      <ul style={{ margin: '0px' }}> {dots} </ul>
    </div>
  ),
  responsive: [
    { breakpoint: 1280, settings: { slidesToShow: 3 } },
    { breakpoint: 1024, settings: { slidesToShow: 2 } },
    { breakpoint: 640, settings: { slidesToShow: 1 } }
  ]
};

const mobileSliderSettings = {
  dots: true,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  rtl: false,
  appendDots: (dots: any) => (
    <div style={{ marginTop: '2.5rem' }}>
      <ul style={{ margin: '0px' }}> {dots} </ul>
    </div>
  ),
};

const StaffGrid = ({ members }: { members: StaffMember[] }) => {
  const sliderRef = useRef<any>(null);
  // Always show carousel on mobile if more than 1 member
  // On desktop, show grid if 4 or fewer members, carousel if more
  return (
    <>
      <div className="md:hidden">
        {members.length > 1 ? (
          <div className="relative px-4 mb-24">
            <div className="relative">
              <Slider {...mobileSliderSettings}>
                {members.map(member => (
                  <div key={member.id} className="px-2">
                    <StaffCard member={member} />
                  </div>
                ))}
              </Slider>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-8">
            {members.map(member => (
              <StaffCard key={member.id} member={member} />
            ))}
          </div>
        )}
      </div>
      <div className="hidden md:block">
        {members.length <= 4 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {members.map(member => (
              <StaffCard key={member.id} member={member} />
            ))}
          </div>
        ) : (
          <div className="relative px-4 mb-24 flex items-center">
            {/* Left arrow - only if more than 4 members */}
            <button
              onClick={() => sliderRef.current?.slickPrev()}
              className="bg-white bg-opacity-80 p-3 rounded-full shadow-lg z-10 hover:bg-opacity-100 transition-all flex-shrink-0 hidden md:block absolute left-0 top-1/2 -translate-y-1/2"
              style={{ left: '-56px' }}
              aria-label="Previous"
            >
              &#8592;
            </button>
            <div className="relative w-full">
              <Slider ref={sliderRef} {...desktopSliderSettings}>
                {members.map(member => (
                  <div key={member.id} className="px-2">
                    <StaffCard member={member} />
                  </div>
                ))}
              </Slider>
            </div>
            {/* Right arrow - only if more than 4 members */}
            <button
              onClick={() => sliderRef.current?.slickNext()}
              className="bg-white bg-opacity-80 p-3 rounded-full shadow-lg z-10 hover:bg-opacity-100 transition-all flex-shrink-0 hidden md:block absolute right-0 top-1/2 -translate-y-1/2"
              style={{ right: '-56px' }}
              aria-label="Next"
            >
              &#8594;
            </button>
          </div>
        )}
      </div>
    </>
  );
};

const StaffSection = () => {
  const { t } = useTranslation();
  const [activeStaffCategory, setActiveStaffCategory] = useState('management');
  return (
    <section id="staff" className="pt-24 pb-32 bg-gradient-to-br from-primary/5 via-primary/10 to-primary/5">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-primary-dark mb-6">
            {t('staff.title')}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t('staff.description')}
          </p>
        </div>
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {['management', 'medical', 'paramedical', 'office'].map((category) => (
            <button
              key={category}
              onClick={() => setActiveStaffCategory(category)}
              className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                activeStaffCategory === category
                  ? 'bg-primary text-gray-800 shadow-lg'
                  : 'bg-white text-primary-dark hover:bg-primary/5'
              }`}
            >
              {t(`staff.categories.${category}.title`)}
            </button>
          ))}
        </div>
        <div className="text-center mb-12">
          <p className="text-lg text-gray-600">
            {t(`staff.categories.${activeStaffCategory}.description`)}
          </p>
        </div>
        <StaffGrid 
          members={t(`staff.members.${activeStaffCategory}`, { returnObjects: true }) as StaffMember[]} 
        />
      </div>
    </section>
  );
};

export default StaffSection; 